import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CarIcon } from '../../../../../assets/icons/car.svg';
import { ReactComponent as ClockIcon } from '../../../../../assets/icons/clock-icon.svg';
import { ReactComponent as NoActiveIcon } from '../../../../../assets/icons/no-active-sessions.svg';
import { ReactComponent as PowerIcon } from '../../../../../assets/icons/power.svg';
import { useAppBranding } from '../../../../../config/branding';
import config from '../../../../../config/config';
import { useBranding } from '../../../../../contexts/BrandingContext';
import { useGoogleMaps } from '../../../../../contexts/google-map-context/GoogleMapContent';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../../../shared-components/button/Button';
import ChargingSpeedSocChart, {
  SocKwApiPoint,
} from '../../../../../shared-components/table-side-panel/component/ChargingSpeedSocChart';
import MeterHardwareStats from '../../../../../shared-components/table-side-panel/component/MeterHardwareStats';
import getFirstSubdomain from '../../../../../shared-functions/DomainsplitFunction';
import ToFixedValue from '../../../../../shared-functions/ToFixedValue';
import endConsumerApi from '../../../../../utils/api/end-consumer-v2';
import profileWebSocketService from '../../../../../websocket/profileWsService';

/** WebSocket SOC payload */
interface SocWsPayload {
  ts: string;
  kW?: number;
  kw?: number;
  soc: number;
}

interface ActiveSessionProperties {
  dataList?: any;
  consumerType?: string;
}

/**
 * Convert lat/lng strings to valid Google map position
 */
const getValidPosition = (lat?: string, lng?: string): google.maps.LatLngLiteral | null => {
  const latitude = Number(lat);
  const longitude = Number(lng);

  if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;

  return { lat: latitude, lng: longitude };
};

/**
 * Format fixed value
 * @param value - Value to be formatted
 * @param digits - Number of digits to be formatted
 * @returns Formatted value
 */
const formatFixed = (value: unknown, digits = 2) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(digits) : '0.00';
};

/**
 * Active session component
 * @returns {React.ReactElement} Active session component
 */
function ActiveChargingSession({
  dataList,
  consumerType,
}: Readonly<ActiveSessionProperties>): React.ReactElement {
  const { brandData } = useBranding();
  const domain = getFirstSubdomain();
  const { t, i18n } = useTranslation('endConsumer');
  const branding = useAppBranding();
  const dispatch = useAppDispatch();
  const [isStop, setIsStop] = useState<boolean>(false);
  const [isForceStop, setIsForceStop] = useState<boolean>(false);
  const [activeSessionInfo, setActiveSessionInfo] = useState<any>(null);
  const [isActiveSession, setIsActiveSession] = useState<boolean>(false);
  const [activeSessionInfoCharger, setActiveSessionInfoCharger] = useState<any>(null);
  const [realTimeChargingData, setRealTimeChargingData] = useState<SocKwApiPoint[]>([]);
  const { selectedEndConsumer, endConsumerType } = useAppSelector((state) => state.endConsumerType);

  const { isLoaded } = useGoogleMaps();

  const mapCenter = useMemo(() => {
    return getValidPosition(
      activeSessionInfoCharger?.chargingStationDetails?.latitude,
      activeSessionInfoCharger?.chargingStationDetails?.longitude,
    );
  }, [
    activeSessionInfoCharger?.chargingStationDetails?.latitude,
    activeSessionInfoCharger?.chargingStationDetails?.longitude,
  ]);

  /**
   * Static map options (memoized once)
   */
  const miniMapOptions = useMemo<google.maps.MapOptions>(
    () => ({
      disableDefaultUI: true,
      clickableIcons: false,
      draggable: false,
      zoomControl: false,
      scrollwheel: false,
    }),
    [],
  );

  /**
   * Clear live session data
   */
  const clearLiveSessionData = () => {
    setActiveSessionInfo(null);
    setActiveSessionInfoCharger(null);
    setRealTimeChargingData([]);
    setIsActiveSession(false);
    setIsStop(false);
    setIsForceStop(false);
  };

  /**
   * Stop transaction
   */
  const handleStopTransaction = async () => {
    const payload = {
      chargingPointSn:
        activeSessionInfoCharger?.chargingPointSnDetails?.chargingPointSn ||
        activeSessionInfo?.chargingPointSn ||
        activeSessionInfoCharger?.chargingPointSn,
      transactionId: activeSessionInfoCharger?.transactionId,
      userCustomId: selectedEndConsumer?.userCustomId,
    };
    try {
      setIsStop(true);
      await endConsumerApi.remoteStopTransaction(payload);
      setTimeout(() => {
        clearLiveSessionData();
      }, 5000);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Remote force stop transaction
   */
  const handleForceStopTransaction = async () => {
    const payload = {
      chargingPointSn:
        activeSessionInfoCharger?.chargingPointSnDetails?.chargingPointSn ||
        activeSessionInfo?.chargingPointSn ||
        activeSessionInfoCharger?.chargingPointSn,
      transactionId: activeSessionInfoCharger?.transactionId,
      userCustomId: selectedEndConsumer?.userCustomId,
      forceStopTransaction: true,
    };
    try {
      setIsForceStop(true);
      await endConsumerApi.remoteStopTransaction(payload);
      setTimeout(() => {
        clearLiveSessionData();
      }, 5000);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Merge new SOC point safely (avoid duplicates)
   */
  const appendSocPoint = useCallback((point: SocWsPayload) => {
    const formatted: SocKwApiPoint = {
      ts: point.ts,
      kw: point.kW ?? point.kw ?? 0,
      soc: point.soc,
    };

    setRealTimeChargingData((previous) => {
      if (previous.some((p) => p.ts === formatted.ts)) return previous;
      return [...previous, formatted];
    });
  }, []);

  /**
   * Populate edit form when entering edit mode
   */
  const handleActiveSession = async () => {
    if (!selectedEndConsumer?.userCustomId) return;

    try {
      const response = await endConsumerApi.fetchActiveSession(selectedEndConsumer.userCustomId, {
        _id: dataList?._id,
      });
      if (
        (endConsumerType === 'commercial' && consumerType !== 'personal') ||
        consumerType === 'commercial'
      ) {
        const session = response?.data?.commercialSessions?.[0] || null;
        setActiveSessionInfoCharger(session);
        if (session?.socVsKW?.length) {
          const history: SocKwApiPoint[] = session.socVsKW.map((p: any) => ({
            ts: p.ts,
            kw: p.kw ?? p.kW ?? 0,
            soc: p.soc,
          }));
          setRealTimeChargingData(history);
        }
      } else {
        const session = response?.data?.personalSessions?.[0] || null;
        setActiveSessionInfoCharger(session);
        if (session?.socVsKW?.length) {
          const history: SocKwApiPoint[] = session.socVsKW.map((p: any) => ({
            ts: p.ts,
            kW: p.kw ?? p.kW ?? 0,
            soc: p.soc,
          }));
          setRealTimeChargingData(history);
        }
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  useEffect(() => {
    if (selectedEndConsumer?.userCustomId) {
      handleActiveSession();
    }
  }, [selectedEndConsumer, endConsumerType]);

  /**
   * WebSocket for RealTime Charging Monitoring
   */
  useEffect(() => {
    if (!activeSessionInfoCharger?.chargingPointSn) return undefined;

    // Ensure we have a leading slash if config.websocketUrl doesn't have one
    const url = domain && brandData?.webSocketUrl ? brandData?.webSocketUrl : config.websocketUrl;
    const baseUrl = url?.endsWith('/') ? url : `${url}/`;

    // Correct URL construction: wss://.../ocpp/admin/userId
    const socketUrl = `${baseUrl}admin/${activeSessionInfoCharger?.chargingPointSn}`;

    /**
     * WebSocket for RealTime Charging Monitoring
     */
    const handleMessage = (data: any) => {
      if (data?.event === 'RealTime Charging Monitoring') {
        setActiveSessionInfo(data);
        setIsActiveSession(true);
        if (data.socVsKW) appendSocPoint(data.socVsKW);
      }
    };

    profileWebSocketService.connect(socketUrl);
    profileWebSocketService.subscribe(handleMessage);

    // eslint-disable-next-line consistent-return
    return () => {
      profileWebSocketService.unsubscribe(handleMessage);
    };
  }, [selectedEndConsumer?.userCustomId, activeSessionInfoCharger, appendSocPoint]);
  return (
    <div className="active-charging-session-wrapper" dir={i18n.dir()}>
      <div className="active-charging-session-header">
        <p className="title">{t('activeChargingSession')}</p>

        {isActiveSession && (
          <div className="header-buttons">
            <MyButton
              label={t('stop')}
              buttonType="force"
              onClickFunc={handleStopTransaction}
              loading={isStop}
              disabled={isStop || isForceStop}
            />
            <MyButton
              label={t('forceStop')}
              buttonType="force"
              onClickFunc={handleForceStopTransaction}
              loading={isForceStop}
              disabled={isStop || isForceStop}
            />
          </div>
        )}
      </div>

      {activeSessionInfoCharger ? (
        <>
          {/* 🔹 TOP LIVE SESSION (WebSocket Data) */}
          <div className="active-charging-session-content">
            <div className="vehicle-details">
              {activeSessionInfoCharger?.vehicleImageUrl ? (
                <img
                  style={{ width: '60px', height: '50px', objectFit: 'contain' }}
                  src={`${activeSessionInfoCharger?.vehicleImageUrl}`}
                  alt={`${activeSessionInfoCharger?.vehicle?.manufacturer} ${activeSessionInfoCharger?.vehicle?.model}`}
                />
              ) : (
                <CarIcon className="vehicle-icon default-icon" />
              )}
              <div className="description">
                <p className="vehicle-brand">
                  {activeSessionInfoCharger?.vehicle?.manufacturer || '--'}
                </p>
                <span className="vehicle-model">
                  {activeSessionInfoCharger?.vehicle?.model || '--'}
                </span>
              </div>
            </div>
          </div>
          {consumerType && (
            <div className="active-charging-session-meter-chart">
              <MeterHardwareStats
                value={activeSessionInfo?.newChargingPercentage || 0}
                finishedTime={
                  activeSessionInfo?.estTime?.minutes
                    ? `${Math.round(activeSessionInfo?.estTime?.minutes || 0)} ${t('min')}`
                    : '--'
                }
                energy={`${activeSessionInfo?.totalKwhConsumed || 0} ${t('kWh')}`}
                completed={t('inProgress')}
                finish={t('estTime')}
              />
            </div>
          )}

          {/* 🔹 GRAPH */}
          <div className="charging-speed-soc-graph">
            <ChargingSpeedSocChart data={realTimeChargingData} available />
          </div>

          <div className="session-stats">
            <div className="stat">
              <p className="label">{t('kwConsumed')}</p>
              <p className="value">
                <PowerIcon className="icon" />
                {formatFixed(activeSessionInfo?.totalKwhConsumed)} {t('kW')}
              </p>
            </div>
            <div className="stat">
              <p className="label">{t('estTime')}</p>
              <p className="value">
                <ClockIcon className="icon" />
                {Math.round(activeSessionInfo?.estTime?.minutes ?? 0)} {t('min')}
              </p>
            </div>
            {consumerType !== 'personal' && (
              <div className="stat">
                <p className="label">{t('liveCost')}</p>
                <p className="value">
                  <branding.icons.currencyIcon className="icon" />
                  {formatFixed(activeSessionInfo?.liveCost)}
                </p>
              </div>
            )}
          </div>

          {consumerType !== 'personal' && endConsumerType !== 'personal' && isLoaded && (
            <div className="mini-map">
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={mapCenter || { lat: 0, lng: 0 }}
                zoom={12}
                options={miniMapOptions}>
                <Marker
                  position={mapCenter || { lat: 0, lng: 0 }}
                  icon={{
                    url: branding.icons.stationLocator,
                    scaledSize: new window.google.maps.Size(32, 32),
                  }}
                />
              </GoogleMap>
            </div>
          )}
          {/* 🔹 STATION + DEVICE INFO */}
          <div className="station-devices-info">
            <div className="info-col">
              <div className="content-list">
                <p className="title">{t('status')} :</p>
                <p className="value">{activeSessionInfoCharger?.status ?? '--'}</p>
              </div>
              {consumerType !== 'personal' && endConsumerType !== 'personal' && (
                <div className="content-list">
                  <p className="title">{t('stationName')} :</p>
                  <p className="value">
                    {activeSessionInfo?.chargingStationName ||
                      activeSessionInfoCharger?.chargingStationDetails?.name ||
                      activeSessionInfo?.stationName ||
                      '--'}
                  </p>
                </div>
              )}

              <div className="content-list">
                <p className="title">{t('device')} :</p>
                <p className="value">
                  {activeSessionInfo?.chargingPointSn ||
                    activeSessionInfoCharger?.chargingPointSnDetails?.chargingPointSn ||
                    activeSessionInfoCharger?.chargingPointSn ||
                    '--'}
                </p>
              </div>
            </div>
            <div className="info-col">
              <div className="content-list">
                <p className="title">{t('chargingSpeed')} :</p>
                <p className="value">
                  {activeSessionInfo?.chargingSpeed
                    ? `${activeSessionInfo.chargingSpeed} ${t('kW')}`
                    : '--'}
                </p>
              </div>
              {/* <div className="content-list">
              <p className="title">Voltage :</p>
              <p className="value">
                {activeSessionInfoCharger?.chargingStationDetails?.name ?? '--'}
              </p>
            </div> */}
              <div className="content-list">
                <p className="title">{t('liveWatts')} :</p>
                <p className="value">
                  {ToFixedValue((activeSessionInfo?.energyConsumedWh ?? 0) / 1000)} {t('kWh')}
                </p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="no-active-session">
          <NoActiveIcon />
          <p>{t('noActiveSession')}</p>
        </div>
      )}
    </div>
  );
}
ActiveChargingSession.defaultProps = {
  dataList: [],
  consumerType: '',
};
export default ActiveChargingSession;
